import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useMemo,
} from 'react'
import PropTypes from 'prop-types'
import formatMessage from 'format-message'
import { Button, Form } from 'react-bootstrap'
import { createAIToolsElement } from '../utils/aiToolsLoader'
import {
  emptySearchHistory,
  normalizeSearchIdentity,
  readSearchHistory,
  writeSearchHistory,
} from '../utils/searchHistory'
import DrawerHeader from './DrawerHeader'

const DrawerSearch = ({ coursenum, userId, userEmail, assistantEnabled }) => {
  const hasUser = !!(userEmail && String(userEmail).trim())
  const normalizedUserId = normalizeSearchIdentity(userId)
  const normalizedCourse = normalizeSearchIdentity(coursenum)
  // Object identity is a generation token, not just an identity comparison:
  // switching A -> B -> A must still invalidate work started by the first A.
  const context = useMemo(
    () => ({ userId: normalizedUserId, coursenum: normalizedCourse }),
    [normalizedUserId, normalizedCourse]
  )
  const currentContextRef = useRef(context)
  currentContextRef.current = context
  const [searchState, setSearchState] = useState(() => ({
    context,
    ...readSearchHistory(context),
    isLoading: false,
    errorMessage: '',
  }))
  const visibleState =
    searchState.context === context
      ? searchState
      : { ...emptySearchHistory(), isLoading: false, errorMessage: '' }
  const {
    searchQuery,
    searchResults,
    totalResults,
    hasSearched,
    isLoading,
    errorMessage,
  } = visibleState
  const stateRef = useRef(visibleState)
  stateRef.current = visibleState
  const updateSearchState = updates =>
    setSearchState(previous =>
      previous.context === context ? { ...previous, ...updates } : previous
    )
  const setSearchQuery = searchQuery => updateSearchState({ searchQuery })
  const setSearchResults = searchResults => updateSearchState({ searchResults })
  const setTotalResults = totalResults => updateSearchState({ totalResults })
  const setErrorMessage = errorMessage => updateSearchState({ errorMessage })

  const aiSearchRef = useRef(null)
  const aiSearchContainerRef = useRef(null)
  const isMountedRef = useRef(true)
  const [aiToolsCreated, setAiToolsCreated] = useState(false)
  const searchInputRef = useRef(null)
  const requestRef = useRef(null)
  const creationRef = useRef(null)
  const aiEnabledRef = useRef(assistantEnabled && hasUser)
  aiEnabledRef.current = assistantEnabled && hasUser

  const MINIMUM_QUERY_LENGTH = 3

  const isCurrentContext = token =>
    isMountedRef.current && currentContextRef.current === token
  const cancelSearch = () => {
    requestRef.current?.cancel()
    requestRef.current = null
  }
  const syncElementConfig = (element, searchConfig = {}) => {
    if (!element) return
    const latest = stateRef.current
    // updateConfig is the web component's atomic, non-DOM configuration API.
    element.updateConfig({
      userid: context.userId,
      coursenum: context.coursenum,
      query:
        searchConfig.query !== undefined
          ? searchConfig.query
          : latest.searchQuery,
      normal_search_number_of_results:
        searchConfig.normal_search_number_of_results !== undefined
          ? searchConfig.normal_search_number_of_results
          : latest.totalResults,
    })
  }

  useLayoutEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
      cancelSearch()
      creationRef.current = null
      if (aiSearchRef.current) {
        aiSearchRef.current.remove()
        aiSearchRef.current = null
      }
    }
  }, [])

  useLayoutEffect(() => {
    cancelSearch()
    creationRef.current = null
    const contextChanged = searchState.context !== context
    const nextSearchState = contextChanged
      ? {
          context,
          ...readSearchHistory(context),
          isLoading: false,
          errorMessage: '',
        }
      : searchState
    if (contextChanged) {
      setSearchState(nextSearchState)
    }
    if (aiSearchRef.current?.isConnected) {
      const shouldHydrateSearch =
        contextChanged && hasUser && nextSearchState.hasSearched
      const nextSearchConfig = shouldHydrateSearch
        ? {
            query: nextSearchState.searchQuery,
            normal_search_number_of_results: nextSearchState.totalResults,
          }
        : { query: '', normal_search_number_of_results: 0 }
      syncElementConfig(aiSearchRef.current, nextSearchConfig)
      aiSearchRef.current.dispatchEvent(
        new CustomEvent('aisearch:query', {
          detail: nextSearchConfig,
        })
      )
    } else {
      aiSearchRef.current = null
      setAiToolsCreated(false)
    }
  }, [context, hasUser])

  useEffect(() => {
    if (searchState.context === context) {
      writeSearchHistory(context, searchState)
    }
  }, [searchState, context])

  useEffect(() => {
    const handleSearchSubmit = event => {
      try {
        const element = aiSearchRef.current
        const eventPath =
          typeof event.composedPath === 'function' ? event.composedPath() : []
        if (
          !element ||
          (event.target !== element && !eventPath.includes(element))
        )
          return
        const submittedQuery = (
          event && event.detail && event.detail.query
            ? String(event.detail.query)
            : ''
        ).trim()
        if (!submittedQuery) return

        // Claim only our own element's request. The VApps sender can then skip
        // its fallback to the legacy page-global search form.
        event.preventDefault()
        setSearchQuery(submittedQuery)
        performSearch(submittedQuery)
      } catch (error) {
        console.warn('Error handling search submission:', error)
      }
    }

    document.addEventListener('drawersearch:submit', handleSearchSubmit)

    return () => {
      document.removeEventListener('drawersearch:submit', handleSearchSubmit)
    }
  }, [context, assistantEnabled])

  useEffect(() => {
    if (
      assistantEnabled &&
      searchQuery &&
      searchResults.length > 0 &&
      aiToolsCreated
    ) {
      syncElementConfig(aiSearchRef.current, {
        query: searchQuery,
        normal_search_number_of_results: totalResults,
      })
      triggerAISearch(searchQuery, totalResults)
    }
  }, [aiToolsCreated, assistantEnabled, searchState.context])

  const validateSearchQuery = query => {
    const trimmedQuery = (query || '').trim()

    if (trimmedQuery.length < MINIMUM_QUERY_LENGTH) {
      setErrorMessage(
        formatMessage(
          'Please enter at least { numberLetters, number } letters.',
          {
            numberLetters: MINIMUM_QUERY_LENGTH,
          }
        )
      )
      setSearchResults([])
      setTotalResults(0)
      return null
    }

    return trimmedQuery
  }

  const createSearchElement = async () => {
    if (
      !assistantEnabled ||
      !aiSearchContainerRef.current ||
      !isMountedRef.current ||
      aiToolsCreated ||
      creationRef.current
    )
      return
    const loader =
      aiSearchContainerRef.current.parentNode.querySelector('.ai-search-loader')

    const creation = { element: null }
    creationRef.current = creation
    const isCurrent = () =>
      isCurrentContext(context) &&
      creationRef.current === creation &&
      aiEnabledRef.current
    // The shared loader awaits bundle registration and may retry. Give it refs
    // belonging to this generation so an obsolete loader cannot append to the
    // current container, overwrite its element ref, or replay its search.
    try {
      await createAIToolsElement({
        containerRef: {
          get current() {
            return isCurrent() ? aiSearchContainerRef.current : null
          },
        },
        elementRef: {
          get current() {
            return creation.element
          },
          set current(element) {
            creation.element = element
            if (isCurrent()) aiSearchRef.current = element
          },
        },
        isMountedRef: {
          get current() {
            return isCurrent()
          },
        },
        setElementCreated: created => {
          if (isCurrent()) setAiToolsCreated(created)
        },
        loaderElement: loader,
        attributes: {
          tooltype: 'aisearch',
          coursenum: context.coursenum,
          userid: context.userId,
          // AISearch reads these constructor props during its first render.
          // The initial query event can otherwise fire before its listener is
          // mounted inside the VApps webcomponent.
          query: stateRef.current.searchQuery,
          normal_search_number_of_results: stateRef.current.totalResults,
        },
        onSuccess: element => {
          if (!isCurrent()) return
          syncElementConfig(element || creation.element)
          const latest = stateRef.current
          if (latest.searchQuery && latest.searchResults.length > 0) {
            triggerAISearch(latest.searchQuery, latest.totalResults)
          }
        },
      })
    } finally {
      // Keep this token valid for the loader's delayed validation callback.
      // Identity changes and unmounts invalidate it instead.
      if (isCurrent() && !creation.element) creationRef.current = null
    }
  }

  useEffect(() => {
    if (assistantEnabled && hasSearched && !aiToolsCreated) {
      createSearchElement()
    }
  }, [assistantEnabled, hasSearched, aiToolsCreated, context, hasUser])

  // Props passed via constructor to keep sensitive data out of DOM
  useEffect(() => {
    if (!aiSearchRef.current) return
    aiSearchRef.current.style.display = hasSearched ? '' : 'none'
  }, [hasSearched, aiToolsCreated])

  useEffect(() => {
    let focusTimeout
    const handleDrawerStateChange = event => {
      const activeDrawer = event && event.detail && event.detail.activeDrawer
      if (activeDrawer === 'search' && hasUser && searchInputRef.current) {
        focusTimeout = setTimeout(() => {
          if (isCurrentContext(context) && searchInputRef.current) {
            searchInputRef.current.focus()
          }
        }, 400)
      }
    }

    document.addEventListener('drawer-state-change', handleDrawerStateChange)
    return () => {
      clearTimeout(focusTimeout)
      document.removeEventListener(
        'drawer-state-change',
        handleDrawerStateChange
      )
    }
  }, [hasUser, context])

  const triggerAISearch = (query, normalSearchResults = 0) => {
    try {
      if (!aiEnabledRef.current || !isCurrentContext(context)) return

      const aiSearchElement = aiSearchRef.current
      if (!aiSearchElement) return

      aiSearchElement.dispatchEvent(
        new CustomEvent('aisearch:query', {
          detail: {
            query: query,
            normal_search_number_of_results: normalSearchResults,
          },
          bubbles: true,
        })
      )
    } catch (error) {
      console.error('Failed to trigger AI search:', error)
    }
  }

  const performSearch = async overrideQuery => {
    const baseQuery =
      typeof overrideQuery === 'string' ? overrideQuery : searchQuery
    const validatedQuery = validateSearchQuery(baseQuery)

    if (!validatedQuery) return

    cancelSearch()
    const controller = new AbortController()
    let earlySearchTimeout
    const request = {
      cancel: () => {
        clearTimeout(earlySearchTimeout)
        controller.abort()
      },
    }
    requestRef.current = request
    const isCurrent = () =>
      isCurrentContext(context) && requestRef.current === request
    updateSearchState({
      searchQuery: validatedQuery,
      errorMessage: '',
      isLoading: true,
      hasSearched: true,
    })

    try {
      let hasTriggeredEarlySearch = false

      earlySearchTimeout = setTimeout(() => {
        if (!isCurrent()) return
        triggerAISearch(validatedQuery, 1)
        hasTriggeredEarlySearch = true
      }, 500)

      const response = await fetch(
        `/reader/search/${context.coursenum}/?searchterms=${encodeURIComponent(
          validatedQuery
        )}&format=json`,
        {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
          signal: controller.signal,
        }
      )

      clearTimeout(earlySearchTimeout)
      if (!isCurrent()) return

      if (!response.ok) throw new Error(`HTTP ${response.status}`)

      const jsonBody = await response.json()
      if (!isCurrent()) return
      const results = Array.isArray(jsonBody.results) ? jsonBody.results : []
      const numResults =
        typeof jsonBody.num_results === 'number' ? jsonBody.num_results : 0

      updateSearchState({ searchResults: results, totalResults: numResults })

      // Keep the committed search in webcomponent config as well as the event
      // channel. This is the fallback when the VApps listener is still
      // starting, and is a no-op for an already synchronized provider.
      syncElementConfig(aiSearchRef.current, {
        query: validatedQuery,
        normal_search_number_of_results: numResults,
      })

      if (!hasTriggeredEarlySearch) {
        triggerAISearch(validatedQuery, numResults)
      }
    } catch (error) {
      if (!isCurrent()) return
      console.error('Search request failed:', error)
      setErrorMessage(formatMessage('Error performing search'))
      setSearchResults([])
      setTotalResults(0)
    } finally {
      clearTimeout(earlySearchTimeout)
      if (isCurrent()) updateSearchState({ isLoading: false })
    }
  }

  const handleInputKeyDown = event => {
    if (event.key === 'Enter') {
      event.preventDefault()
      performSearch()
    }
  }

  const clearSearch = () => {
    cancelSearch()
    updateSearchState({
      ...emptySearchHistory(),
      isLoading: false,
      errorMessage: '',
    })
    syncElementConfig(aiSearchRef.current, {
      query: '',
      normal_search_number_of_results: 0,
    })
    triggerAISearch('', 0)
  }

  const renderSearchForm = () => (
    <React.Fragment>
      <div className="drawer-search-input">
        <Form.Control
          ref={el => (searchInputRef.current = el)}
          type="text"
          placeholder={formatMessage('Search textbook...')}
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          onKeyDown={handleInputKeyDown}
          aria-label={formatMessage('Search entire text')}
        />
      </div>

      <div className="drawer-footer">
        <Button variant="primary" onClick={performSearch}>
          {formatMessage('Search')}
        </Button>
        {hasSearched && (
          <Button
            variant="secondary"
            onClick={clearSearch}
            style={{ marginLeft: '10px' }}
          >
            {formatMessage('Clear')}
          </Button>
        )}
      </div>
      {assistantEnabled && (
        <hr className="ai-search-divider" role="separator" aria-hidden="true" />
      )}
    </React.Fragment>
  )

  const renderAISearchSection = () => (
    <div className="ai-search-section">
      <div
        ref={aiSearchContainerRef}
        style={{ position: 'relative', width: '100%' }}
      ></div>
    </div>
  )

  const renderRegularResultsList = () => (
    <React.Fragment>
      {isLoading && (
        <div className="drawer-loading">
          <i className="fa fa-spinner fa-spin" aria-hidden="true"></i>
          <span className="sr-only">
            {formatMessage('Loading search results')}
          </span>
        </div>
      )}

      {!isLoading && totalResults > 0 && (
        <React.Fragment>
          <span
            className="label label-default"
            role="status"
            aria-live="polite"
          >
            {formatMessage('Found {count} {label}', {
              count: totalResults,
              label:
                totalResults === 1
                  ? formatMessage('Match')
                  : formatMessage('Matches'),
            })}
          </span>
          {searchResults.map((result, index) => (
            <div
              key={index}
              className="search-result-item"
              role="listitem"
              aria-labelledby={`search-result-${index}`}
              aria-describedby={`search-def-${index}`}
            >
              <p
                id={`search-result-${index}`}
                className="search-result-title"
                aria-label={formatMessage('Search result: {title}', {
                  title: result.subtitle,
                })}
              >
                <span>{result.subtitle}</span>
                {result.url && (
                  <a
                    href={result.url}
                    className="glossary-term-link btn btn-myed btn-link-myed btn-xs-myed"
                    aria-label={formatMessage('View {title} in book', {
                      title: result.title,
                    })}
                  >
                    {formatMessage('View')}
                    <i
                      className="fa-light fa-external-link-alt ml-2"
                      aria-hidden="true"
                    ></i>
                  </a>
                )}
              </p>
              <p
                id={`search-def-${index}`}
                className="search-result-definition"
                aria-label={formatMessage('Content: {text}', {
                  text: result.text,
                })}
                dangerouslySetInnerHTML={{ __html: result.text }}
              />
            </div>
          ))}
        </React.Fragment>
      )}

      {!isLoading && hasSearched && totalResults === 0 && (
        <div className="drawer-no-results" role="status" aria-live="polite">
          {formatMessage('No matches found')}
        </div>
      )}
    </React.Fragment>
  )

  return (
    <div className="drawer-search">
      <DrawerHeader title={formatMessage('Search')} />

      {!hasUser ? (
        <div className="drawer-empty-state p-3">
          <p className="text-muted text-p-margin-removed">
            {formatMessage('You must be logged in to use search.')}
          </p>
        </div>
      ) : (
        <React.Fragment>
          {errorMessage && (
            <div className="drawer-section">
              <p className="text-danger no-margin">{errorMessage}</p>
            </div>
          )}
          {renderSearchForm()}
          <div className="search-results" role="region" aria-live="polite">
            {assistantEnabled && renderAISearchSection()}
            {renderRegularResultsList()}
          </div>
        </React.Fragment>
      )}
    </div>
  )
}

DrawerSearch.propTypes = {
  coursenum: PropTypes.string.isRequired,
  userId: PropTypes.string,
  userEmail: PropTypes.string,
  assistantEnabled: PropTypes.bool.isRequired,
}

export default DrawerSearch
